import {
  addTransferOrderFiles,
  addTransferOrderNote,
  editMultiTransferOrderLines,
  editTransferOrder,
  getPoLinePurchaseOrders,
  getPurchaseOrderLineProducts,
  getToLineTransferOrders,
  getTransferOrderLineProducts,
  getTransferOrderLineStatus,
  setMOAllocations,
  setPurchaseAllocations,
  setSalesOrderAllocations,
  setTOLinesConsolidatePOIds,
  setTransferAllocations,
  setTransferOrder,
  setTransferOrderBills,
  setTransferOrderFiles,
  setTransferOrderLines,
  setTransferOrderNotes,
  setTransferOrders,
  setTransferOrdersSelection,
  setTransferOrderStatus,
  updateAllocationsAfterAddToShipment,
  updateMOAllocatedQty,
  updatePurchaseAllocatedQty,
  updatePurchaseOrderAllocationStatus,
  updateSalesOrderAllocatedQty,
  updateSOAllocationStatusForRelease,
  updateTransferAllocatedQty,
  updateTransferOrderAllocationStatus,
  updateTransferOrderLines,
  updateTransferOrderLineStatus,
} from "../slice/orderSlice";
import {
  setAllTransferOrders,
  setOrderlineMapById,
  setPurchaseAllocationForRule,
  setTransferAllocationForRule,
  updatePurchaseAllocatedQtyFromTransferV2,
  updateTransferAllocatedQtyFromTransferV2,
} from "../slice/TransferOrderSliceV2";
import { apiSlice } from "./apiSlice";
import { fileResponseHandler } from "./utils";

export const orderApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    addOrderLinesToConsignment: builder.mutation({
      query: ({ consignment_id, ...data }) => ({
        url: `/api_shipment/consignment/${consignment_id}/add_to_shipment/`,
        method: "PATCH",
        body: data,
      }),
      onQueryStarted: (data, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(updateAllocationsAfterAddToShipment(data)))
          .catch(error => {});
      },
    }),
    addTfOrdLinesToPO: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api_po/purchase_order/${id}/bulk_update/`,
        method: "PATCH",
        body: data,
      }),
      onQueryStarted: (data, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(updateTransferOrderLines({ ...result.data, ...data })))
          .catch(error => {});
      },
    }),
    autoStockAllocation: builder.mutation({
      query: () => ({ url: "/api_warehouse/auto_allocation/", method: "POST", body: {} }),
    }),
    cargoReadyPurchaseOrderLines: builder.mutation({
      query: data => ({
        url: "/api_po/allocation/bulk_update_status_to_cargo_ready/",
        method: "PUT",
        body: data,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(orderApiSlice.util.invalidateTags(["allocatedPurchaseList"]));
        } catch (error) {}
      },
    }),
    cargoReadyTransferOrderLines: builder.mutation({
      query: data => ({
        url: "/api_order/allocation/bulk_update_status_to_cargo_ready/",
        method: "PUT",
        body: data,
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(addTransferOrderNote(result.data)))
          .catch(error => {});
      },
    }),
    createPlnOrdeFromProjectedStock: builder.mutation({
      query: data => ({ url: "/api_order/planned_order/", method: "POST", body: data }),
    }),
    createTransferOrder: builder.mutation({
      query: data => ({
        url: "/api_order/v2/tf_ord/",
        method: "POST",
        body: data,
      }),
    }),
    createShipmentFromPurchaseAllocation: builder.mutation({
      query: data => ({
        url: "/api_shipment/create_purchase_consignment/",
        method: "POST",
        body: data,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(orderApiSlice.util.invalidateTags(["allocatedPurchaseList"]));
          dispatch(orderApiSlice.util.invalidateTags(["purchaseOrderList"]));
        } catch (error) {}
      },
    }),
    previewPurchaseOrderConsignment: builder.mutation({
      query: data => ({
        url: "/api_shipment/preview_purchase_order_consignment/",
        method: "POST",
        body: data,
      }),
    }),
    getOpenPurchaseShipments: builder.query({
      query: toWarehouseIds =>
        `/api_shipment/consignment/open_purchase_selection/?to_warehouse_ids=${encodeURIComponent(
          toWarehouseIds,
        )}`,
    }),
    addPurchaseLinesToConsignment: builder.mutation({
      query: ({ consignment_id, ...data }) => ({
        url: `/api_shipment/consignment/${consignment_id}/add_purchase_lines/`,
        method: "PATCH",
        body: data,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(orderApiSlice.util.invalidateTags(["allocatedPurchaseList"]));
          dispatch(orderApiSlice.util.invalidateTags(["purchaseOrderList"]));
        } catch (error) {}
      },
    }),
    generatePicklistFromSalesOrderAllocation: builder.mutation({
      query: data => ({
        url: "/api/warehouse/v1/picklist/generate/",
        method: "POST",
        body: data,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(orderApiSlice.util.invalidateTags(["allocatedSalesOrderList"]));
        } catch (error) {}
      },
    }),
    createShipmentFromTransferAllocation: builder.mutation({
      query: data => ({
        url: "/api_shipment/create_transfer_consignment/",
        method: "POST",
        body: data,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(orderApiSlice.util.invalidateTags(["allocatedTransferList"]));
        } catch (error) {}
      },
    }),
    createTransferOrderBill: builder.mutation({
      query: data => ({ url: `/api_invoice/bill/`, method: "POST", body: data }),
    }),
    createTransferOrderFiles: builder.mutation({
      query: data => ({
        url: `/api_docdigit/document/create_tf_ord_files/`,
        method: "POST",
        body: data,
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(addTransferOrderFiles(result.data)))
          .catch(error => {});
      },
    }),
    createTransferOrderNote: builder.mutation({
      query: data => ({ url: "/api_chat/note/", method: "POST", body: data }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(addTransferOrderNote(result.data)))
          .catch(error => {});
      },
    }),
    downloadTransferOrderPdf: builder.query({
      query: arg => {
        const id = arg && typeof arg === "object" ? arg.id : arg;
        const layoutId = arg && typeof arg === "object" ? arg.layoutId : null;
        const suffix = layoutId ? `?layout_id=${encodeURIComponent(layoutId)}` : "";
        return {
          url: `/api_order/transfer_order/${id}/download_pdf/${suffix}`,
          responseHandler: fileResponseHandler,
        };
      },
    }),
    downloadTransferOrderExcel: builder.query({
      query: arg => {
        const id = arg && typeof arg === "object" ? arg.id : arg;
        const layoutId = arg && typeof arg === "object" ? arg.layoutId : null;
        const suffix = layoutId ? `?layout_id=${encodeURIComponent(layoutId)}` : "";
        return {
          url: `/api_order/transfer_order/${id}/download_excel/${suffix}`,
          responseHandler: fileResponseHandler,
        };
      },
    }),
    shareTransferOrder: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api_order/transfer_order/${id}/share/`,
        method: "POST",
        body: data,
      }),
    }),
    getBillsForTransferOrder: builder.query({
      query: params => `/api_invoice/bill/?${params}`,
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setTransferOrderBills(result.data)))
          .catch(error => {});
      },
    }),
    getFilesForTransferOrder: builder.query({
      query: params => `/api_docdigit/document/?${params}`,
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setTransferOrderFiles(result.data)))
          .catch(error => {});
      },
    }),
    getFilterPurchaseAllocationByRule: builder.query({
      query: ({ id, params }) => `/api_po/allocation/?view_id=${id}&${params}`,
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setPurchaseAllocationForRule(result.data)))
          .catch(error => {});
      },
      providesTags: _ => ["allocatedPurchaseList"],
    }),
    getFilterTransferAllocationByRule: builder.query({
      query: ({ id, params }) => `/api_order/allocation/?view_id=${id}&${params}`,
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setTransferAllocationForRule(result.data)))
          .catch(error => {});
      },
    }),
    getSalesOrderAllocations: builder.query({
      query: params =>
        params ? `/api/sale/v1/so_allocation/?${params}` : "/api/sale/v1/so_allocation/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => {
            dispatch(setSalesOrderAllocations(result.data));
          })
          .catch(error => {});
      },
      providesTags: _ => ["allocatedSalesOrderList"],
    }),
    getMOAllocations: builder.query({
      query: params =>
        params
          ? `/api/production/v1/mo_allocation/?${params}`
          : "/api/production/v1/mo_allocation/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => {
            dispatch(setMOAllocations(result.data));
          })
          .catch(error => {});
      },
      providesTags: _ => ["allocatedMOList"],
    }),
    getPoLinePurchaseOrders: builder.query({
      query: (params = null) =>
        params ? `/api_po/get_purchase_orders?${params}` : "/api_po/get_purchase_orders/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(getPoLinePurchaseOrders(result.data)))
          .catch(error => {});
      },
    }),
    getPurchaseAllocations: builder.query({
      query: params => (params ? `/api_po/allocation/?${params}` : "/api_po/allocation"),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => {
            dispatch(setPurchaseAllocations(result.data));
            dispatch(setOrderlineMapById(result.data.results));
          })
          .catch(error => {});
      },
      providesTags: _ => ["allocatedPurchaseList"],
    }),
    getPurchaseOrderLineProducts: builder.query({
      query: () => "/api_po/purchase_order_line_products/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(getPurchaseOrderLineProducts(result.data.data)))
          .catch(error => {});
      },
    }),
    getTfOrdLineForCnsldPO: builder.query({
      query: params => ({ url: "/api_order/v2/tf_ord_line/for_consolidated_po/", params: params }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setTOLinesConsolidatePOIds(data));
        } catch (error) {}
      },
    }),
    getToLineTransferOrders: builder.query({
      query: () => "/api_order/get_transfer_orders/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(getToLineTransferOrders(result.data.data)))
          .catch(error => {});
      },
    }),
    getTransferAllocations: builder.query({
      query: params => (params ? `/api_order/allocation/?${params}` : "/api_order/allocation"),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => {
            dispatch(setTransferAllocations(result.data));
            dispatch(setOrderlineMapById(result.data.results));
          })
          .catch(error => {});
      },
      providesTags: _ => ["allocatedTransferList"],
    }),
    getTransferOrder: builder.query({
      query: id => `/api_order/transfer_order/${id}/`,
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(setTransferOrder(result.data))).catch(error => {});
      },
      providesTags: (result, error, id) => [{ type: "transferOrderDetail", id }],
    }),
    getTransferOrderHistory: builder.query({
      query: ({ id, limit = 20, offset = 0 }) => ({
        url: `/api_order/transfer_order/${id}/history/`,
        params: { limit, offset },
      }),
    }),
    getTransferOrderLineProducts: builder.query({
      query: () => "/api_order/transfer_order_line_products/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(getTransferOrderLineProducts(result.data.data)))
          .catch(error => {});
      },
    }),
    getTransferOrderLines: builder.query({
      query: params =>
        params ? `/api_order/transfer_order_line/?${params}` : "/api_order/transfer_order_line/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setTransferOrderLines(result.data)))
          .catch(error => {});
      },
    }),
    getTransferOrderLineStatus: builder.query({
      query: () => "/api_order/transfer_order_line_status/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(getTransferOrderLineStatus(result.data)))
          .catch(error => {});
      },
    }),
    getTransferOrderNotes: builder.query({
      query: params => (params ? `/api_chat/note/?${params}` : "/api_chat/note/"),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setTransferOrderNotes(result.data)))
          .catch(error => {});
      },
    }),
    getTransferOrders: builder.query({
      query: params =>
        params ? `/api_order/transfer_order/?${params}` : "/api_order/transfer_order/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => {
            dispatch(setTransferOrders(result.data));
            dispatch(setAllTransferOrders(result.data.results));
          })
          .catch(error => {});
      },
      providesTags: _ => ["transferOrderList"],
    }),
    getTransferOrdersSelection: builder.query({
      query: params =>
        params ? `/api_order/transfer_order/?${params}` : "/api_order/transfer_order/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setTransferOrdersSelection(result.data)))
          .catch(error => {});
      },
      providesTags: _ => ["transferOrderList"],
    }),
    getTransferOrderStatus: builder.query({
      query: () => "/api_order/transfer_order_status/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setTransferOrderStatus(result.data)))
          .catch(error => {});
      },
    }),
    getTransferOrdersV2: builder.query({
      query: params => (params ? `/api_order/v2/tf_ord/?${params}` : "/api_order/v2/tf_ord/"),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => {
            dispatch(setTransferOrders(result.data));
          })
          .catch(error => {});
      },
    }),
    importTransferOrder: builder.mutation({
      query: () => ({ url: "/api_order/import_transfer_order", method: "POST", body: {} }),
    }),
    mutatePurchaseAllocatedQty: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api_po/po_line/${id}/update_allocated_qty/`,
        method: "PATCH",
        body: data,
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => {
            dispatch(updatePurchaseAllocatedQty(result.data));
            dispatch(updatePurchaseAllocatedQtyFromTransferV2(result.data));
          })
          .catch(error => {});
      },
    }),
    mutateTransferAllocatedQty: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api_order/transfer_order_line/${id}/update_allocated_qty/`,
        method: "PATCH",
        body: data,
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => {
            dispatch(updateTransferAllocatedQty(result.data));
            dispatch(updateTransferAllocatedQtyFromTransferV2(result.data));
          })
          .catch(error => {});
      },
    }),
    mutateSalesOrderAllocatedQty: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/sale/v1/so_allocation/${id}/update-allocated-qty/`,
        method: "PATCH",
        body: data,
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => {
            dispatch(updateSalesOrderAllocatedQty(result.data));
          })
          .catch(error => {});
      },
    }),
    mutateMOAllocatedQty: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/production/v1/mo_allocation/${id}/update-allocated-qty/`,
        method: "PATCH",
        body: data,
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => {
            dispatch(updateMOAllocatedQty(result.data));
          })
          .catch(error => {});
      },
    }),
    mutateTransferOrderLineStatus: builder.mutation({
      query: data => ({
        url: `/api_order/transfer_order_line/update_status/`,
        method: "PATCH",
        body: data,
      }),
      onQueryStarted: (data, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(() => dispatch(updateTransferOrderLineStatus(data))).catch(error => {});
      },
    }),
    confirmDraftTransferOrders: builder.mutation({
      query: data => ({
        url: "/api_order/transfer_order/confirm_draft/",
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["transferOrderList"],
    }),
    releaseSalesOrderAllocation: builder.mutation({
      query: ({ id }) => ({
        url: `/api/sale/v1/so_allocation/${id}/release-allocation/`,
        method: "PATCH",
        body: {},
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(updateSOAllocationStatusForRelease(result.data)))
          .catch(error => {});
      },
    }),
    releaseMOAllocation: builder.mutation({
      query: ({ id }) => ({
        url: `/api/production/v1/mo_allocation/${id}/release-allocation/`,
        method: "PATCH",
        body: {},
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(updateMOAllocatedQty(result.data)))
          .catch(error => {});
      },
    }),
    splitPOLines: builder.mutation({
      query: data => ({ url: "/api_po/v2/po_line/bulk_split/", method: "PATCH", body: data }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(orderApiSlice.util.invalidateTags(["allocatedPurchaseList"]));
        } catch (error) {}
      },
    }),
    splitTfOrdLines: builder.mutation({
      query: data => ({
        url: "/api_order/v2/tf_ord_line/bulk_split/",
        method: "PATCH",
        body: data,
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(orderApiSlice.util.invalidateTags(["allocatedTransferList"]));
        } catch (error) {}
      },
    }),
    updateDispatchPurchaseAllocations: builder.mutation({
      query: data => ({
        url: "/api_po/allocation/bulk_update_dispatched_qty/",
        method: "PATCH",
        body: data,
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => {
            dispatch(orderApiSlice.util.invalidateTags(["allocatedPurchaseList"]));
          })
          .catch(error => {});
      },
    }),
    updateDispatchTfAllocations: builder.mutation({
      query: data => ({
        url: "/api_order/allocation/bulk_update_dispatched_qty/",
        method: "PATCH",
        body: data,
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => {
            dispatch(orderApiSlice.util.invalidateTags(["allocatedTransferList"]));
          })
          .catch(error => {});
      },
    }),
    updatePurchaseOrderAllocationStatus: builder.mutation({
      query: data => ({
        url: "/api_po/allocation/update_purchase_order_allocation_status/",
        method: "PATCH",
        body: data,
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => {
            dispatch(updatePurchaseOrderAllocationStatus(result.data.data));
            dispatch(orderApiSlice.util.invalidateTags(["allocatedPurchaseList"]));
          })
          .catch(error => {});
      },
    }),
    updateTransferOrder: builder.mutation({
      query: ({ id, data }) => ({
        url: `/api_order/transfer_order/${id}/update_transfer_order/`,
        method: "PATCH",
        body: data,
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(editTransferOrder(result.data.data)))
          .catch(error => {});
      },
    }),
    updateTransferOrderAllocationStatus: builder.mutation({
      query: data => ({
        url: "/api_order/allocation/update_transfer_order_allocation_status/",
        method: "PATCH",
        body: data,
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => {
            dispatch(updateTransferOrderAllocationStatus(result.data.data));
            dispatch(orderApiSlice.util.invalidateTags(["allocatedTransferList"]));
          })
          .catch(error => {});
      },
    }),
    updateTransferOrderLinesETA: builder.mutation({
      query: data => ({
        url: "/api_order/transfer_order_line/bulk_update_eta/",
        method: "PATCH",
        body: data,
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(editMultiTransferOrderLines(result.data)))
          .catch(error => {});
      },
    }),
    updateTransferOrderName: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api_order/transfer_order/${id}/update_transfer_order_name/`,
        method: "PATCH",
        body: data,
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(editTransferOrder(result.data))).catch(error => {});
      },
    }),
  }),
});

export const {
  useAddOrderLinesToConsignmentMutation,
  useAddTfOrdLinesToPOMutation,
  useAutoStockAllocationMutation,
  useCargoReadyPurchaseOrderLinesMutation,
  useCargoReadyTransferOrderLinesMutation,
  useConfirmDraftTransferOrdersMutation,
  useCreatePlnOrdeFromProjectedStockMutation,
  useCreateTransferOrderMutation,
  useCreateShipmentFromPurchaseAllocationMutation,
  usePreviewPurchaseOrderConsignmentMutation,
  useGetOpenPurchaseShipmentsQuery,
  useAddPurchaseLinesToConsignmentMutation,
  useGeneratePicklistFromSalesOrderAllocationMutation,
  useCreateShipmentFromTransferAllocationMutation,
  useCreateTransferOrderBillMutation,
  useCreateTransferOrderFilesMutation,
  useCreateTransferOrderNoteMutation,
  useGetBillsForTransferOrderQuery,
  useGetFilesForTransferOrderQuery,
  useGetFilterPurchaseAllocationByRuleQuery,
  useGetFilterTransferAllocationByRuleQuery,
  useGetPoLinePurchaseOrdersQuery,
  useGetPurchaseAllocationsQuery,
  useGetPurchaseOrderLineProductsQuery,
  useLazyGetTfOrdLineForCnsldPOQuery,
  useGetToLineTransferOrdersQuery,
  useGetTransferAllocationsQuery,
  useGetSalesOrderAllocationsQuery,
  useGetMOAllocationsQuery,
  useGetTransferOrderLineProductsQuery,
  useGetTransferOrderLinesQuery,
  useGetTransferOrderLineStatusQuery,
  useGetTransferOrderNotesQuery,
  useGetTransferOrderQuery,
  useGetTransferOrderHistoryQuery,
  useGetTransferOrdersQuery,
  useGetTransferOrdersSelectionQuery,
  useGetTransferOrderStatusQuery,
  useGetTransferOrdersV2Query,
  useImportTransferOrderMutation,
  useLazyDownloadTransferOrderPdfQuery,
  useLazyDownloadTransferOrderExcelQuery,
  useShareTransferOrderMutation,
  useLazyGetTransferOrderLineStatusQuery,
  useLazyGetTransferOrdersSelectionQuery,
  useLazyGetTransferOrderStatusQuery,
  useMutatePurchaseAllocatedQtyMutation,
  useMutateTransferAllocatedQtyMutation,
  useMutateSalesOrderAllocatedQtyMutation,
  useMutateMOAllocatedQtyMutation,
  useMutateTransferOrderLineStatusMutation,
  useReleaseSalesOrderAllocationMutation,
  useReleaseMOAllocationMutation,
  useSplitPOLinesMutation,
  useSplitTfOrdLinesMutation,
  useUpdateDispatchPurchaseAllocationsMutation,
  useUpdateDispatchTfAllocationsMutation,
  useUpdatePurchaseOrderAllocationStatusMutation,
  useUpdateTransferOrderAllocationStatusMutation,
  useUpdateTransferOrderLinesETAMutation,
  useUpdateTransferOrderMutation,
  useUpdateTransferOrderNameMutation,
} = orderApiSlice;
